import { ExtendedAccount } from "@common/types/reports/types";
import { useEntitiesById } from "@hooks/useEntities";
import {
    isKickDefaultAccount,
    resolveDefaultAccountName,
} from "@common/constants/domains/accounting/defaultAccountNames";
import { useBulkUpdateAccountsMutation } from "@api/account.api";
import { PermissionGuardedMenuItem } from "./PermissionGuardedMenuItem";

interface Props {
    account: ExtendedAccount;
    similarAccounts: ExtendedAccount[];
    canEditCoA: boolean;
}

export const RevertToDefaultNameMenuItem = ({
    account,
    similarAccounts,
    canEditCoA,
}: Readonly<Props>) => {
    const entitiesById = useEntitiesById({ onlyBusiness: true });
    const bulkUpdateAccounts = useBulkUpdateAccountsMutation();

    const isKickDefault = isKickDefaultAccount(account);

    const accountEntityType =
        entitiesById.get(account.entityId)?.legalType ?? null;

    const canonicalDefaultName =
        isKickDefault && accountEntityType && account.kickOriginalCode != null
            ? resolveDefaultAccountName({
                  kickOriginalCode: account.kickOriginalCode,
                  entityType: accountEntityType,
              })
            : null;

    const canRevertToDefaultName =
        isKickDefault &&
        canonicalDefaultName != null &&
        canonicalDefaultName !== account.name;

    if (!canRevertToDefaultName) {
        return null;
    }

    const handleRevertToDefaultName = () => {
        if (bulkUpdateAccounts.isPending) {
            return;
        }
        const accountsToRevert =
            similarAccounts.length > 0 ? similarAccounts : [account];
        const accountsByCanonicalName = new Map<
            string,
            Array<{ accountId: string; entityId: number }>
        >();
        for (const similarAccount of accountsToRevert) {
            if (!isKickDefaultAccount(similarAccount)) {
                continue;
            }
            const entityType = entitiesById.get(
                similarAccount.entityId,
            )?.legalType;
            if (!entityType) {
                continue;
            }
            const canonicalName = resolveDefaultAccountName({
                kickOriginalCode: similarAccount.kickOriginalCode,
                entityType,
            });
            if (!canonicalName || canonicalName === similarAccount.name) {
                continue;
            }
            const grouped = accountsByCanonicalName.get(canonicalName) ?? [];
            grouped.push({
                accountId: similarAccount.id,
                entityId: similarAccount.entityId,
            });
            accountsByCanonicalName.set(canonicalName, grouped);
        }
        for (const [name, accounts] of accountsByCanonicalName) {
            bulkUpdateAccounts.mutate({ accounts, name });
        }
    };

    return (
        <PermissionGuardedMenuItem
            isAllowed={canEditCoA}
            onClick={handleRevertToDefaultName}
        >
            Revert to default name
        </PermissionGuardedMenuItem>
    );
};
